import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ChevronDownIcon} from '@/components/ui/icon/Icon'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {useTranslation} from '@/translations/i18n'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {ReactNode, useState} from 'react'
import {Controller, useFieldArray, useFormContext} from 'react-hook-form'
import {Label} from '@/components/ui/label/Label'
import {Divider} from '@/components/ui/divider/Divider'
import {Contact} from '@/types'
import {patientContactLabelsMapToLabel} from '@/features/patients/utils'
import {PatientContactsFormValidationSchema} from '@/features/patients/types'
import {ContactComboboxLabel} from '@/components/commons/contact-combobox-label/ContactComboboxLabel'
import {ContactPhoneField} from '@/components/commons/contact-phone-field/ContactPhoneField'

export const PatientContactsFields = ({title}: {title?: ReactNode}) => {
    const {t} = useTranslation()
    const [isOpen, setOpen] = useState(false)

    // methods
    const form = useFormContext<PatientContactsFormValidationSchema>()
    const {fields, append, remove} = useFieldArray({
        name: 'contacts',
        control: form.control
    })

    return (
        <Flexbox direction="column" gap={6} fullWidth>
            {title && (
                <Flexbox fullWidth justify="space-between">
                    {fields.length > 0 && title}
                    <AddContactDropdown isOpen={isOpen} setOpen={setOpen} append={append} />
                </Flexbox>
            )}

            <Flexbox direction="column" fullWidth>
                {fields.map((field, index) => (
                    <Flexbox key={field.id} direction="column" gap={1.5} fullWidth>
                        <Label>
                            {field.type == 'email'
                                ? t('commons:email')
                                : field.type == 'telephone'
                                  ? t('commons:telephone')
                                  : t('commons:mobile_phone')}
                        </Label>
                        <Flexbox gap={2.5} fullWidth>
                            {field.type == 'email' ? (
                                <InputText
                                    id="contact-email"
                                    type="email"
                                    placeholder="esempioemail@email.it"
                                    maxLength={254}
                                    data-anti-autofill="true"
                                    autoComplete="off"
                                    errorMessage={
                                        form?.formState?.errors?.contacts &&
                                        form.formState.errors.contacts[index]?.contact?.message
                                    }
                                    {...form.register(`contacts.${index}.contact`)}
                                />
                            ) : (
                                <ContactPhoneField index={index} />
                            )}
                            <Controller
                                control={form.control}
                                name={`contacts.${index}.label`}
                                render={({field}) => (
                                    <ContactComboboxLabel
                                        index={index}
                                        value={field.value}
                                        onChange={field.onChange}
                                        remove={index > 1 ? remove : null}
                                        optionsMap={patientContactLabelsMapToLabel}
                                        errorMessage={form?.formState?.errors?.contacts?.[index]?.label?.message}
                                    />
                                )}
                            />
                        </Flexbox>
                        {index < fields.length - 1 && (
                            <Divider direction="horizontal" startSpacing={4} endSpacing={4} />
                        )}
                    </Flexbox>
                ))}
            </Flexbox>

            {!title && <AddContactDropdown isOpen={isOpen} setOpen={setOpen} append={append} />}
        </Flexbox>
    )
}

const AddContactDropdown = ({
    isOpen,
    setOpen,
    append
}: {
    isOpen: boolean
    setOpen: (value: boolean) => void
    append: (contact: {contact: string; label: string; type: Contact['type']}) => void
}) => {
    const {t} = useTranslation()
    return (
        <MenuProvider open={isOpen} setOpen={setOpen}>
            <MenuButton
                render={
                    <Button size="sm" shape="rectangle" variant="secondary">
                        {t('commons:add_contact')}
                        <ChevronDownIcon />
                    </Button>
                }
            />
            <Menu sameWidth>
                <MenuGroup>
                    <MenuItem onClick={() => append({contact: '', label: '', type: 'mobile_phone'})}>
                        {t('commons:mobile_phone')}
                    </MenuItem>
                    <MenuItem onClick={() => append({contact: '', label: '', type: 'telephone'})}>
                        {t('commons:telephone')}
                    </MenuItem>
                    <MenuItem onClick={() => append({contact: '', label: '', type: 'email'})}>
                        {t('commons:email')}
                    </MenuItem>
                </MenuGroup>
            </Menu>
        </MenuProvider>
    )
}
